import { Link, Outlet, useLocation } from 'react-router-dom';
import { BrandMark, Icon, type IconName } from '../components/ui';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useT, type MessageKey } from '../i18n';
import { authColumnClass } from '../lib/navigation';

/**
 * Login, registro y recuperación.
 *
 * El armazón pone la página completa, la tarjeta y el `<main>`; las pantallas
 * de dentro ponen sólo su formulario. Antes cada una repetía las tres cosas, y
 * el resultado era una tarjeta dentro de otra —doble borde, doble relleno— y
 * dos altos de pantalla encajados, que es de donde salía el aire de más
 * alrededor del formulario.
 *
 * El ancho lo decide la ruta: el alta enseña el catálogo de perfiles y necesita
 * sitio para la rejilla; las demás se leen mejor estrechas.
 */

/**
 * Las tres razones que se enseñan mientras alguien decide si se registra.
 *
 * Son las mismas de la franja de confianza de la portada, y salen del mismo
 * sitio del catálogo: quien llega aquí desde la portada acaba de leerlas, y
 * quien llega directo desde un enlace compartido no las ha visto nunca. Repetir
 * la promesa en la puerta es lo que evita que el formulario sea el primer sitio
 * donde el producto no dice nada de sí mismo.
 */
const RAZONES: { icon: IconName; title: MessageKey; body: MessageKey }[] = [
  { icon: 'shield', title: 'trust.encryptionTitle', body: 'trust.encryptionBody' },
  { icon: 'device', title: 'trust.mfaTitle', body: 'trust.mfaBody' },
  { icon: 'download', title: 'trust.exportTitle', body: 'trust.exportBody' },
];

export function AuthLayout() {
  const t = useT();
  const { pathname } = useLocation();

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-12">
      {/*
        Dos anchos, y no uno: el formulario se lee mejor estrecho, pero las
        tres razones heredaban ese mismo ancho y salían a palabra por línea
        —`sm:grid-cols-3` mira el ancho de la ventana, no el del contenedor—.
        El envoltorio es siempre ancho y sólo la columna del formulario se
        estrecha.
      */}
      <div className="w-full max-w-3xl">
        <div className={`mx-auto w-full ${authColumnClass(pathname)}`}>
          {/*
          La marca entera, no el símbolo pequeño que había: es la única
          pantalla donde el visitante está parado, decidiendo, y mirando un
          formulario. Enlaza a la portada porque quien llega aquí por error
          necesita una salida que no sea el botón de atrás.
        */}
          <header className="mb-8 flex flex-col items-center">
            <Link to="/" className="rounded-xl" aria-label="29100 Cloud — inicio">
              <BrandMark animated interactive className="w-full max-w-[17rem]" />
            </Link>
            <p className="mt-2 text-sm text-muted">{t('common.tagline')}</p>
          </header>

          <main className="surface p-6 sm:p-8">
            <Outlet />
          </main>
        </div>

        <ul className="mt-10 grid gap-6 sm:grid-cols-3">
          {RAZONES.map((razon) => (
            <li key={razon.title} className="flex gap-3">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-brand/15 text-primary-soft">
                <Icon name={razon.icon} className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold">{t(razon.title)}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-muted">{t(razon.body)}</p>
              </div>
            </li>
          ))}
        </ul>

        {/* Visible también sin sesión: quien no entiende la pantalla no puede entrar a cambiarlo. */}
        <div className="mt-8 flex justify-center">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}
